import { withRouter } from "react-router-dom";
import React, { Fragment } from "react";
import { compose, withHandlers } from "recompose";
import { connect } from "react-redux";
import styled from "styled-components";
import {
  AnchorButton,
  Colors,
  Menu,
  MenuItem,
  MenuDivider,
  Popover,
  Position,
  Button,
  Tooltip,
  Classes
} from "@blueprintjs/core";
import { maxWidth, sizes, getScreenCode } from "app/media";

import CustomLink from "./CustomLink";
import logo_src from "app/resources/globe.png";
import { withAuth } from "app/auth/hocs";

import { FormattedMessage, injectIntl } from "react-intl";
import { updateIntl } from "react-intl-redux";
import { messages } from "app/utils";

const urlSearch = window.location.search;

export const MenuLink = props => {
  const { className, children, ...rest } = props;
  return (
    <CustomLink
      className={`pt-minimal ${className}`}
      activeClassName="pt-active"
      as={AnchorButton}
      {...rest}
    >
      {children}
    </CustomLink>
  );
};

const MenuLogo = styled(MenuLink)`
  margin-top: -2px;
  & .name {
    color: ${Colors.GRAY1};
    display: inline-block;
    text-transform: uppercase;
    margin-left: 4px;
    margin-right: 10px;
  }
  & img {
    display: inline-block;
    vertical-align: middle;
    margin-top: -2px;
    height: 28px;
  }
`;

const Breadcrumbs = styled.div`
  margin-left: -10px;
`;

/*
const DebugSize = styled.div`
  position: absolute;
  top: 1px;
  left: 1px;
  background: rgba(256, 125, 0, 0.5);
  padding: 1px 3px;
  border-radius: 3px;
  font-size: 10px;
`;
*/

const UserMenu = injectIntl(props => {
  const { user, onClick, intl } = props;
  const menu = (
    <Menu>
      <MenuItem
        text={intl.formatMessage({
          id: "auth.logout",
          defaultMessage: "Logout"
        })}
        onClick={onClick}
        iconName="log-out"
      />
    </Menu>
  );
  return (
    <span>
      <Popover content={menu} position={Position.BOTTOM_RIGHT}>
        <AnchorButton iconName="user" className="pt-minimal" text={user.name} />
      </Popover>
    </span>
  );
});

const LanguagesMenu = ({
  changeLanguage,
  intl,
  showText,
  position,
  showTooltip
}) => {
  const languageButtonText = intl.formatMessage({
    id: "app.language.button",
    defaultMessage: "Change language..."
  });

  const languagesButton = showText ? (
    <Button text={languageButtonText} className={Classes.MINIMAL} />
  ) : (
    <Button iconName="translate" className={Classes.MINIMAL} />
  );

  const languagesButtonWithTooltip = showTooltip ? (
    <Tooltip content={languageButtonText} position={Position.LEFT}>
      {languagesButton}
    </Tooltip>
  ) : (
    languagesButton
  );

  return (
    <Popover
      content={
        <Menu>
          <MenuItem onClick={() => changeLanguage("en-GB")} text="English" />
          <MenuItem onClick={() => changeLanguage("it")} text="Italiano" />
          <MenuItem onClick={() => changeLanguage("de")} text="Deutsch" />
          <MenuItem onClick={() => changeLanguage("el")} text="Ελληνικά" />
          <MenuItem onClick={() => changeLanguage("ur")} text="اردو" />
          <MenuItem onClick={() => changeLanguage("es")} text="Español" />
        </Menu>
      }
      position={position}
    >
      {languagesButtonWithTooltip}
    </Popover>
  );
};

const Nav = styled.div`
  @media ${maxWidth("small")} {
  }
`;

const navigate = props => location => () => {
  props.history.push(location);
};

const HamburgerMenu = withHandlers({ navigate })(props => {
  const {
    isAuthenticated,
    logout,
    contextMenu,
    loginLocation,
    navigate,
    intl,
    changeLanguage
  } = props;
  const tetherOptions = {
    constraints: [{ attachment: "together", to: "scrollParent" }]
  };
  const menu = (
    <Menu>
      {contextMenu}
      {contextMenu && <MenuDivider />}
      <LanguagesMenu
        changeLanguage={changeLanguage}
        intl={intl}
        showText
        position={Position.BOTTOM}
      />
      <MenuDivider />
      {isAuthenticated ? (
        <Fragment>
          <MenuItem text="Logout" iconName="log-out" onClick={logout} />
        </Fragment>
      ) : (
        <Fragment>
          <MenuItem
            text={intl.formatMessage({
              id: "app.signup",
              defaultMessage: "Sign up"
            })}
            onClick={navigate("/signup" + urlSearch)}
          />
          <MenuItem
            text={intl.formatMessage({
              id: "app.login",
              defaultMessage: "Log in"
            })}
            onClick={navigate(loginLocation)}
          />
        </Fragment>
      )}
    </Menu>
  );
  return (
    <div className="pt-navbar-group pt-align-right">
      <Popover
        content={menu}
        tetherOptions={tetherOptions}
        position={Position.BOTTOM_RIGHT}
      >
        <AnchorButton iconName="menu" className="pt-minimal" />
      </Popover>
    </div>
  );
});

const DesktopMenu = props => {
  const {
    isAuthenticated,
    user,
    logout,
    contextMenu,
    loginLocation,
    changeLanguage,
    intl
  } = props;
  return (
    <Fragment>
      {contextMenu && (
        <Fragment>
          {contextMenu}
          <span className={Classes.NAVBAR_DIVIDER} />
        </Fragment>
      )}
      {isAuthenticated ? (
        <Fragment>
          <UserMenu user={user} onClick={logout} />
        </Fragment>
      ) : (
        <Fragment>
          <MenuLink to={"/signup" + urlSearch}>
            <FormattedMessage id="app.signup" defaultMessage="Sign up" />
          </MenuLink>
          <MenuLink to={loginLocation.pathname + urlSearch}>
            <FormattedMessage id="app.login" defaultMessage="Log in" />
          </MenuLink>
        </Fragment>
      )}
      <span className={Classes.NAVBAR_DIVIDER} />
      <LanguagesMenu
        changeLanguage={changeLanguage}
        intl={intl}
        position={Position.BOTTOM_RIGHT}
        showTooltip
      />
    </Fragment>
  );
};

const MainMenu = props => {
  const {
    auth: { isAuthenticated, user },
    logout,
    location,
    contextMenu,
    screen,
    breadcrumbs,
    topicId,
    history,
    desktopExtra,
    intl,
    changeLanguage
  } = props;
  const urlSearch = window.location.search;
  const loginLocation = {
    pathname: "/login",
    search: urlSearch,
    state: { from: location }
  };
  const menuProps = {
    history,
    isAuthenticated,
    user,
    loginLocation,
    contextMenu: (topicId || "").match(/rbkc/) ? undefined : contextMenu,
    logout,
    screen,
    intl,
    changeLanguage
  };
  const showBreadcrumbs = Boolean(breadcrumbs) && screen > sizes.tiny;
  const showName = !showBreadcrumbs || screen >= sizes.xlarge;
  return (
    <Nav className="pt-navbar">
      {/*<DebugSize>{((sizes[screen] || "")[0] || "").toUpperCase()}</DebugSize>*/}
      <div className="pt-navbar-group pt-align-left">
        <MenuLogo
          to={"/" + urlSearch}
          className="pt-minimal pt-navbar-heading"
          isActive={false}
          dataset={{ showName }}
        >
          <img src={logo_src} alt="logo" />
          {showName && <span className="name">Smarticipate</span>}
        </MenuLogo>
        {showBreadcrumbs && <Breadcrumbs>{breadcrumbs}</Breadcrumbs>}
      </div>
      <div className="pt-navbar-group pt-align-right">
        {screen >= sizes.xsmall && desktopExtra ? desktopExtra : null}
        {screen > sizes.xsmall ? (
          <DesktopMenu {...menuProps} />
        ) : (
          <HamburgerMenu {...menuProps} />
        )}
      </div>
    </Nav>
  );
};

const mapScreenState = state => {
  return { screen: getScreenCode(state.responsive) };
};

const enhance = compose(
  withRouter,
  withAuth,
  injectIntl,
  connect(
    null,
    dispatch => ({
      changeLanguage: locale =>
        dispatch(updateIntl({ locale, messages: messages[locale] }))
    })
  ),
  connect(mapScreenState)
);

export default enhance(MainMenu);



// WEBPACK FOOTER //
// ./src/app/MainMenu.js